import PropTypes from 'prop-types';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { constants, price } from '@eti/utils';
import { ResultFilter } from '@eti/types';
import { useTranslation } from '@eti/providers';
import { SelectedFilters } from '../../../constants/resultPropTypes';
import CheckboxFilter from '../v1/CheckboxFilter';
import RangeSliderFilter from '../RangeSliderFilter';
import { formatDateTimeValue, useDurationFormatter } from '../utils/searchFilters';
import NumOfFlightsLabel from '../NumOfFlightsLabel';
import ResetButton from '../v1/ResetButton';

const filterStyles = (isLabelTooLong) => css`
  header {
    padding: 10px 0;
  }

  ul {
    text-align: initial;

    @media ${mediaQueries.medium.up} {
      column-count: ${isLabelTooLong ? 1 : 2};
      column-gap: 10px;
    }

    @media (min-width: 2370px) {
      column-count: ${isLabelTooLong ? 2 : 3};
      column-gap: 10px;
    }
  }

  @media ${mediaQueries.medium.up} {
    border-top: 1px dotted ${etiColors.grayScale[9]};
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const debugFiltersHeaderStyles = css`
  color: ${etiColors.black};
  font-size: 1rem;
  font-weight: 700;
  padding: 1rem;
`;

const debugFiltersContentStyles = css`
  border-top: 1px solid rgb(236 236 236 / 80%);
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  padding: 1rem;
  text-align: center;
`;

const useTypeFormatters = () => {
  const { formatDurationValue } = useDurationFormatter();
  const dateTimeFormat = 'yyyy-MM-dd HH:mm:ss';

  return {
    AMOUNT: price.formatPrice,
    DURATION: formatDurationValue,
    NUMBER: (value) => value,
    TEXT: (value) => value,
    TIME: formatDateTimeValue(dateTimeFormat),
  };
};

const DebugFilters = ({
  availableFilters,
  filteredFlightsCount,
  flightsCount,
  onFilter,
  onResetAll,
  selectedFilters,
}) => {
  const { t } = useTranslation();
  const formatters = useTypeFormatters();

  return (
    <>
      <div className={debugFiltersHeaderStyles}>
        <span>{t('Responsive.Air.Result.Filter.Button.Filter')}</span>
        <NumOfFlightsLabel
          filteredFlightsCount={filteredFlightsCount}
          flightsCount={flightsCount}
        />
        {flightsCount !== filteredFlightsCount && (
          <ResetButton
            dataTestId="resultPage-debugFilters-filterHeader-allFilterResetButton-button"
            onClick={onResetAll}
          />
        )}
      </div>
      <div className={debugFiltersContentStyles} data-testid="resultPage-debugFilters-content">
        {availableFilters
          .filter((filter) =>
            filter.__typename === 'RangeFilter'
              ? filter.min !== filter.max
              : filter.options.length > 1,
          )
          .map((filter) => (
            <div key={filter.code}>
              {filter.__typename === 'RangeFilter' ? (
                <RangeSliderFilter
                  className={filterStyles()}
                  formatDisplayedValue={formatters[filter.type]}
                  isInitiallyExpanded={false}
                  isToggleable
                  label={filter.label.replace('Air.Filter.Type.', '')}
                  maxValue={filter.max}
                  minValue={filter.min}
                  name={filter.code}
                  onFilter={onFilter}
                  selectedValues={selectedFilters[filter.code]}
                />
              ) : (
                <CheckboxFilter
                  alwaysExpandedOnDesktop={false}
                  className={filterStyles(
                    filter.options.some((filterOption) => filterOption.label.length > 19),
                  )}
                  label={filter.label.replace('Air.Filter.Type.', '')}
                  name={filter.code}
                  onFilter={onFilter}
                  options={filter.options.map((option) => ({
                    code: option.value,
                    name: option.label || constants.unicodeControlCharacters.NBSP,
                  }))}
                  selectedValues={selectedFilters[filter.code]}
                />
              )}
            </div>
          ))}
      </div>
    </>
  );
};

DebugFilters.propTypes = {
  availableFilters: PropTypes.arrayOf(ResultFilter),
  filteredFlightsCount: PropTypes.number,
  flightsCount: PropTypes.number,
  onFilter: PropTypes.func,
  onResetAll: PropTypes.func,
  selectedFilters: SelectedFilters,
};

export default DebugFilters;
